import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RankBadge } from "@/components/RankBadge";
import { Mail, Send, Swords, User as UserIcon, ArrowLeft } from "lucide-react";
import { ChallengeModal } from "@/pages/FindOpponent";

export default function Messages() {
  const { otherId } = useParams();
  if (otherId) return <Thread otherId={otherId} />;
  return <Inbox />;
}

const Inbox = () => {
  const [convs, setConvs] = useState([]);
  const load = () => api.get("/chat/conversations").then(({ data }) => setConvs(data));
  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="text-cyan-300" />
          <h1 className="font-display font-bold text-3xl">Tin nhắn</h1>
        </div>
        {convs.length === 0 ? (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-12 text-center text-white/40">
            Chưa có cuộc trò chuyện. Vào <Link to="/find" className="text-cyan-300 underline">Tìm đối thủ</Link> rồi bấm <Mail size={12} className="inline" /> để nhắn riêng.
          </div>
        ) : (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
            {convs.map((c) => (
              <Link key={c.other.id} to={`/chat/dm/${c.other.id}`} className="flex items-center gap-3 p-4 border-b border-white/5 hover:bg-white/[0.03]" data-testid={`conv-${c.other.id}`}>
                <img src={c.other.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold truncate">{c.other.nickname}</span>
                    <RankBadge elo={c.other.elo} size="sm" showElo={false} />
                  </div>
                  <div className="text-xs text-white/50 truncate mt-0.5">{c.last_from_me && "Bạn: "}{c.last_message}</div>
                </div>
                <div className="text-[10px] text-white/40">{new Date(c.last_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Thread = ({ otherId }) => {
  const { user } = useAuth();
  const [partner, setPartner] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [challengeOpen, setChallengeOpen] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/chat/dm/${otherId}?limit=80`).then(({ data }) => setMsgs(data));

  useEffect(() => {
    api.get(`/chat/dm/${otherId}/info`).then(({ data }) => setPartner(data));
    load();
    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "chat.dm" && ev.participants?.includes(user?.id) && ev.participants?.includes(otherId)) {
            setMsgs((m) => [...m, ev.msg].slice(-200));
          }
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
    // eslint-disable-next-line
  }, [otherId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    const m = text.trim();
    if (!m) return;
    try {
      await api.post(`/chat/dm/${otherId}`, { message: m });
      setText("");
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#05050a] flex flex-col">
      <TopNav />
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-4 flex flex-col">
        {partner && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#0c0d14] border border-white/8 mb-3">
            <Link to="/chat" className="p-1 hover:bg-white/5 rounded"><ArrowLeft size={16} /></Link>
            <img src={partner.avatar_url} alt="" className="w-10 h-10 rounded-full" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold truncate">{partner.nickname}</span>
                <RankBadge elo={partner.elo} size="sm" showElo={false} />
              </div>
              <div className="text-[10px] text-white/40">@{partner.username}</div>
            </div>
            <button onClick={() => setChallengeOpen(true)} data-testid="dm-challenge" className="px-3 py-1.5 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/25 flex items-center gap-1">
              <Swords size={12} /> Thách đấu
            </button>
            <Link to={`/players/${partner.id}`} className="px-3 py-1.5 rounded text-xs border border-white/10 hover:bg-white/5 flex items-center gap-1">
              <UserIcon size={12} /> Hồ sơ
            </Link>
          </div>
        )}

        <div className="flex-1 rounded-xl bg-[#0c0d14] border border-white/8 p-4 overflow-y-auto space-y-3 mb-3 min-h-[400px] max-h-[60vh]" data-testid="dm-list">
          {msgs.length === 0 ? (
            <div className="text-white/40 text-center py-12 text-sm">Bắt đầu cuộc trò chuyện</div>
          ) : msgs.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm break-words ${mine ? "bg-cyan-400/15 border border-cyan-400/30" : "bg-white/[0.04] border border-white/8"}`}>
                  {m.message}
                  <div className="text-[10px] text-white/40 mt-0.5">{new Date(m.created_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Nhắn tin riêng..." data-testid="dm-input"
            className="flex-1 bg-[#0c0d14] border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-cyan-400/60" />
          <button type="submit" data-testid="dm-send" className="px-5 rounded-md bg-gradient-to-r from-cyan-400 to-purple-500 text-black font-medium flex items-center gap-2">
            <Send size={14} />
          </button>
        </form>
      </div>

      {challengeOpen && partner && <ChallengeModal target={partner} onClose={() => setChallengeOpen(false)} />}
    </div>
  );
};
